/** People Analytics — one mega sheet: hours, workload, compliance, points, AI summaries. */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { PersonChip } from "@/components/PersonChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sparkles, Download, ChevronDown, ChevronUp, RefreshCw, Scale, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };

const COMP_BANDS = { all: () => true, clean: (r) => r.violations === 0, at_risk: (r) => r.violations > 0 && !r.escalated, escalated: (r) => r.escalated > 0 };
const UTIL_BANDS = { all: () => true, low: (r) => r.utilization_pct < 40, mid: (r) => r.utilization_pct >= 40 && r.utilization_pct < 80, high: (r) => r.utilization_pct >= 80 };

export default function PeopleAnalytics() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [from, setFrom] = useState(daysAgo(14));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [comp, setComp] = useState("all");
  const [util, setUtil] = useState("all");
  const [sort, setSort] = useState({ key: "hours", dir: -1 });
  const [open, setOpen] = useState({});

  const load = () => api.get(`/workspaces/${wsId}/people-analytics?from=${from}&to=${to}`)
    .then((r) => setData(r.data)).catch((e) => toast.error(apiErrorMessage(e)));
  useEffect(() => { if (wsId) load(); }, [wsId]); // eslint-disable-line

  const depts = useMemo(() => [...new Set((data?.rows || []).map((r) => r.department).filter(Boolean))], [data]);
  const rows = useMemo(() => {
    let out = (data?.rows || []).filter((r) =>
      (!q || (r.name || "").toLowerCase().includes(q.toLowerCase()) || (r.email || "").toLowerCase().includes(q.toLowerCase()))
      && (dept === "all" || r.department === dept)
      && COMP_BANDS[comp](r) && UTIL_BANDS[util](r));
    out = [...out].sort((a, b) => {
      const av = a[sort.key] ?? -1, bv = b[sort.key] ?? -1;
      return sort.dir === -1 ? (bv > av ? 1 : bv < av ? -1 : 0) : (av > bv ? 1 : av < bv ? -1 : 0);
    });
    return out;
  }, [data, q, dept, comp, util, sort]);

  const sortBy = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }));
  const exportCsv = () => {
    const cols = ["name", "department", "designation", "hours", "billable_pct", "utilization_pct", "upcoming_planned_hours", "violations", "compliance_rate", "pending_adjustments", "points_total", "rank", "open_tasks", "overdue_tasks", "on_time_pct"];
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `people-analytics-${from}-${to}.csv`;
    a.click();
  };

  return (
    <>
      <PageHeader eyebrow="HRMS" title="People Analytics" description="One sheet for the whole company — activity, workload, compliance, scores & AI insights." testid="people-header" />
      <div className="px-4 sm:px-6 md:px-10 pb-10">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-38" data-testid="people-from" />
          <span className="text-sm text-muted-foreground">→</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-38" data-testid="people-to" />
          <Button size="sm" className="rounded-none" onClick={load} data-testid="people-apply">Apply</Button>
          <Input placeholder="Search name/email…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none w-48" data-testid="people-search" />
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="rounded-none w-36" data-testid="people-filter-dept"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All depts</SelectItem>{depts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={comp} onValueChange={setComp}>
            <SelectTrigger className="rounded-none w-36" data-testid="people-filter-comp"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All compliance</SelectItem><SelectItem value="clean">Clean</SelectItem>
              <SelectItem value="at_risk">Has violations</SelectItem><SelectItem value="escalated">Escalated</SelectItem>
            </SelectContent>
          </Select>
          <Select value={util} onValueChange={setUtil}>
            <SelectTrigger className="rounded-none w-36" data-testid="people-filter-util"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All utilization</SelectItem><SelectItem value="low">Low &lt;40%</SelectItem>
              <SelectItem value="mid">40–80%</SelectItem><SelectItem value="high">High ≥80%</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="rounded-none ml-auto" onClick={exportCsv} data-testid="people-export-csv"><Download className="w-4 h-4 mr-1" />CSV</Button>
        </div>
        <Rebalancer wsId={wsId} onApplied={load} />
        <NlReporter wsId={wsId} departments={depts} />
        {data?.scope === "team" && <p className="text-xs text-muted-foreground mb-2">Showing your direct reports only.</p>}
        <div className="overflow-x-auto border">
          <table className="w-full text-sm whitespace-nowrap" data-testid="people-table">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-2.5 font-medium">Member</th>
                {[["hours", "Hours"], ["utilization_pct", "Util %"], ["billable_pct", "Billable %"],
                  ["upcoming_planned_hours", "Next 7d"], ["open_tasks", "Open"], ["overdue_tasks", "Overdue"],
                  ["violations", "Violations"], ["compliance_rate", "Comp %"], ["pending_adjustments", "Pending"],
                  ["points_total", "Points"], ["on_time_pct", "On-time %"]].map(([k, label]) => (
                  <th key={k} className="p-2.5 font-medium text-right cursor-pointer select-none hover:text-primary" onClick={() => sortBy(k)} data-testid={`people-sort-${k}`}>
                    {label}{sort.key === k ? (sort.dir === -1 ? " ↓" : " ↑") : ""}
                  </th>
                ))}
                <th className="p-2.5 font-medium text-center">AI</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.user_id} r={r} wsId={wsId} open={!!open[r.user_id]}
                     toggle={() => setOpen((o) => ({ ...o, [r.user_id]: !o[r.user_id] }))} onChanged={load} />
              ))}
              {data && rows.length === 0 && <tr><td className="p-4 text-muted-foreground" colSpan={13}>No members match the filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Row({ r, wsId, open, toggle, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [deep, setDeep] = useState(null);
  const [deepBusy, setDeepBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/members/${r.user_id}/ai-summary`);
      toast.success(`AI summary refreshed for ${r.name}`);
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const generateDeep = async () => {
    setDeepBusy(true);
    try {
      const { data } = await api.post(`/hrms/workspaces/${wsId}/employees/${r.user_id}/performance-summary`);
      setDeep(data);
      toast.success(`Deep 90-day summary generated for ${r.name}`);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setDeepBusy(false); }
  };
  const compTone = r.escalated ? "text-red-600" : r.violations ? "text-amber-600" : "text-emerald-600";
  return (
    <>
      <tr className="border-t hover:bg-muted/30" data-testid={`people-row-${r.user_id}`}>
        <td className="p-2.5">
          <PersonChip userId={r.user_id} name={r.name} className="font-medium" />
          <span className="block text-[11px] text-muted-foreground">{[r.designation, r.department].filter(Boolean).join(" · ")}</span>
        </td>
        <td className="p-2.5 text-right font-mono">{r.hours}</td>
        <td className={`p-2.5 text-right font-mono ${r.utilization_pct < 40 ? "text-amber-600" : ""}`}>{r.utilization_pct}%</td>
        <td className="p-2.5 text-right font-mono">{r.billable_pct}%</td>
        <td className="p-2.5 text-right font-mono">{r.upcoming_planned_hours}h</td>
        <td className="p-2.5 text-right font-mono">{r.open_tasks}</td>
        <td className={`p-2.5 text-right font-mono ${r.overdue_tasks ? "text-red-600 font-semibold" : ""}`}>{r.overdue_tasks}</td>
        <td className={`p-2.5 text-right font-mono ${compTone}`}>{r.violations}{r.escalated ? " ⚠" : ""}</td>
        <td className={`p-2.5 text-right font-mono ${compTone}`}>{r.compliance_rate}%</td>
        <td className="p-2.5 text-right font-mono">{r.pending_adjustments}</td>
        <td className="p-2.5 text-right font-mono">{r.points_total}{r.rank ? <span className="text-muted-foreground text-[10px]"> #{r.rank}</span> : ""}</td>
        <td className="p-2.5 text-right font-mono">{r.on_time_pct}%</td>
        <td className="p-2.5 text-center">
          <button onClick={toggle} className="text-violet-600 hover:text-violet-500 inline-flex items-center gap-0.5" data-testid={`people-ai-toggle-${r.user_id}`}>
            <Sparkles className="w-4 h-4" />{open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="border-t bg-violet-500/5" data-testid={`people-ai-row-${r.user_id}`}>
          <td colSpan={13} className="p-3">
            {r.ai_summary ? (
              <div className="text-xs space-y-2 max-w-4xl whitespace-normal">
                <p className="text-sm">{r.ai_summary.summary}</p>
                <div className="flex flex-wrap gap-4">
                  {[["strengths", "Strengths", "text-emerald-600"], ["risks", "Risks", "text-red-600"], ["talking_points", "1:1 talking points", "text-violet-600"]].map(([k, label, tone]) => (
                    (r.ai_summary[k] || []).length > 0 && (
                      <div key={k} className="min-w-52">
                        <p className={`text-[10px] uppercase tracking-wide ${tone}`}>{label}</p>
                        <ul className="list-disc pl-4 mt-0.5 space-y-0.5">{r.ai_summary[k].map((x, i) => <li key={i}>{x}</li>)}</ul>
                      </div>
                    )
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground">Generated {r.ai_summary_at?.slice(0, 16).replace("T", " ")} UTC</p>
              </div>
            ) : <p className="text-xs text-muted-foreground">No AI summary yet.</p>}
            <Button size="sm" variant="outline" className="rounded-none mt-2 h-7 text-xs" onClick={generate} disabled={busy} data-testid={`people-ai-generate-${r.user_id}`}>
              {busy ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              {busy ? "Generating…" : r.ai_summary ? "Refresh summary" : "Generate summary"}
            </Button>
            <Button size="sm" variant="outline" className="rounded-none mt-2 ml-2 h-7 text-xs border-primary/50 text-primary hover:bg-primary/10" onClick={generateDeep} disabled={deepBusy} data-testid={`people-deep-generate-${r.user_id}`}>
              {deepBusy ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              {deepBusy ? "Analyzing 90d…" : "Deep 90-day insight"}
            </Button>
            {deep && (
              <div className="mt-3 p-3 border border-primary/40 bg-primary/5 text-xs space-y-2 max-w-4xl whitespace-normal" data-testid={`people-deep-${r.user_id}`}>
                <p className="text-sm font-medium">{deep.content.summary}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[["strengths", "Strengths", "text-emerald-600"], ["development_areas", "Development areas", "text-amber-600"], ["coaching_topics", "Coaching topics", "text-primary"]].map(([k, label, tone]) => (
                    (deep.content[k] || []).length > 0 && (
                      <div key={k}>
                        <p className={`text-[10px] uppercase tracking-wide ${tone}`}>{label}</p>
                        <ul className="list-disc pl-4 mt-0.5 space-y-0.5">{deep.content[k].map((x, i) => <li key={i}>{x}</li>)}</ul>
                      </div>
                    )
                  ))}
                </div>
                <details className="text-[10px] text-muted-foreground">
                  <summary className="cursor-pointer">Signals used</summary>
                  <pre className="mt-2 p-2 bg-muted/40 rounded text-[10px] overflow-x-auto">{JSON.stringify(deep.signals, null, 2)}</pre>
                </details>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Rebalancer({ wsId, onApplied }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState({});
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/people-analytics/rebalance`);
      setResult(data); setApplied({});
      if (!data.suggestions.length) toast.info(data.assessment || "Team load looks balanced — no moves suggested");
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const apply = async (s) => {
    try {
      await api.patch(`/tasks/${s.task_id}`, { assignee_id: s.to_user_id });
      setApplied((a) => ({ ...a, [s.task_id]: true }));
      toast.success(`"${s.task_title}" reassigned to ${s.to_name}`);
      onApplied();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <div className="mb-4">
      <Button size="sm" variant="outline" className="rounded-none border-violet-500/50 text-violet-600 hover:text-violet-500" onClick={run} disabled={busy} data-testid="rebalance-btn">
        {busy ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Scale className="w-4 h-4 mr-1" />}
        {busy ? "Analyzing workloads…" : "AI workload rebalance"}
      </Button>
      {result && result.suggestions.length > 0 && (
        <div className="border border-violet-500/40 bg-violet-500/5 p-3 mt-3 space-y-2" data-testid="rebalance-panel">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm">{result.assessment}</p>
            <button onClick={() => setResult(null)} className="text-muted-foreground hover:text-foreground shrink-0" data-testid="rebalance-close"><X className="w-4 h-4" /></button>
          </div>
          {result.suggestions.map((s) => (
            <div key={s.task_id} className="border bg-background p-2.5 flex items-center gap-3 text-sm" data-testid={`rebalance-sug-${s.task_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{s.task_title}{s.remaining_hours != null && <span className="text-xs text-muted-foreground font-mono"> · ~{s.remaining_hours}h left</span>}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">{s.from_name} <ArrowRight className="w-3 h-3 shrink-0" /> <b>{s.to_name}</b> — {s.reason}</p>
              </div>
              {applied[s.task_id]
                ? <span className="text-xs text-emerald-600 font-semibold shrink-0">Applied ✓</span>
                : <Button size="sm" className="rounded-none h-7 text-xs shrink-0" onClick={() => apply(s)} data-testid={`rebalance-apply-${s.task_id}`}>Reassign</Button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NlReporter({ wsId, departments }) {
  const [q, setQ] = useState("");
  const [windowDays, setWindowDays] = useState(90);
  const [depts, setDepts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const SUGGESTIONS = [
    "Who has the highest utilization this quarter?",
    "Which employees have >3 compliance violations?",
    "Show me top 5 performers by points",
    "Anyone with attendance dropping >20% month-over-month?",
  ];
  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/hrms/workspaces/${wsId}/reports/natural-language`, {
        question: text, departments: depts.length ? depts : undefined, time_window_days: Number(windowDays),
      });
      setAnswer(data);
      setQ("");  // clear only on success — preserve on error so user can retry
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const toggleDept = (d) => setDepts((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);
  return (
    <div className="mb-4 border border-primary/40 bg-primary/5 p-3" data-testid="nl-reporter">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold">Ask a workforce question</span>
        </div>
        <button onClick={() => setExpanded((v) => !v)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="nl-expand-toggle">
          {expanded ? "Collapse" : "Expand"}
        </button>
      </div>
      {expanded && (
        <div className="flex flex-wrap gap-2 mb-2">
          {SUGGESTIONS.map((s, i) => (
            <button key={i} onClick={() => ask(s)} disabled={busy}
                    className="text-xs px-2 py-1 border rounded-none hover:bg-primary/10"
                    data-testid={`nl-suggestion-${i}`}>{s}</button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                 placeholder="e.g. Who has the highest utilization?"
                 className="rounded-none flex-1" data-testid="nl-input" />
        <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
          <SelectTrigger className="rounded-none w-32" data-testid="nl-window"><SelectValue /></SelectTrigger>
          <SelectContent>{[30, 60, 90, 180, 365].map((n) => <SelectItem key={n} value={String(n)}>Last {n}d</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" className="rounded-none" onClick={() => ask()} disabled={busy || !q.trim()} data-testid="nl-ask">
          {busy ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
          {busy ? "Thinking…" : "Ask"}
        </Button>
      </div>
      {expanded && departments.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          <span className="text-[10px] text-muted-foreground uppercase mr-1 self-center">Departments:</span>
          {departments.map((d) => (
            <button key={d} onClick={() => toggleDept(d)}
                    className={`text-[11px] px-2 py-0.5 border rounded-none ${depts.includes(d) ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}
                    data-testid={`nl-dept-${d}`}>{d}</button>
          ))}
        </div>
      )}
      {answer && (
        <div className="mt-3 p-3 border bg-background text-sm whitespace-pre-wrap" data-testid="nl-answer">
          <p className="text-[10px] text-muted-foreground uppercase mb-1">Answer · {answer.row_count} employees analyzed</p>
          {answer.answer}
        </div>
      )}
    </div>
  );
}

